import { createReducer } from '@reduxjs/toolkit'
import { WritableDraft } from 'immer'

import { Conversation } from '@/model/conversation'

import {
  clearConversationUnread,
  conversationNamespace,
  ConversationQuery,
  createConversation,
  DEFAULT_QUERY,
  readAllConversations,
  removeConversation,
  SuggestedChatResult,
  updateConversationQuery,
} from './conversation-actions'
import { updateHomeTabFanUserId } from './creator-tab-actions'
import { requestChatCompletions, upsertMessage } from './message-actions'

function updateConversationQueryMap(
  draft: typeof initialState,
  creatorId: number,
  newQuery: Partial<ConversationQuery>,
) {
  const chatQueryMap = draft.conversationQueryMap[creatorId]
  if (chatQueryMap) {
    draft.conversationQueryMap[creatorId] = { ...chatQueryMap, ...newQuery }
  } else {
    draft.conversationQueryMap[creatorId] = { ...DEFAULT_QUERY, ...newQuery }
  }
}

function handleChatCompletionStatusChange(
  creatorId: number,
  fanUserId: number,
  draft: WritableDraft<typeof initialState>,
  fetching: boolean,
) {
  const existSuggestedChatMap = draft.suggestedChatMap[creatorId]
  if (existSuggestedChatMap) {
    const existSuggestedChat = existSuggestedChatMap[fanUserId]
    if (existSuggestedChat) {
      existSuggestedChat.suggestionFetching = fetching
    } else {
      existSuggestedChatMap[fanUserId] = {
        suggestionFetching: fetching,
        choices: [],
      }
    }
  } else {
    draft.suggestedChatMap[creatorId] = {
      [fanUserId]: {
        suggestionFetching: fetching,
        choices: [],
      },
    }
  }
}

const initialState: {
  conversationsMap: Record<number, Conversation[] | undefined>
  suggestedChatMap: Record<
    number,
    Record<number, SuggestedChatResult | undefined> | undefined
  >,
  conversationQueryMap: Record<number, ConversationQuery | undefined>,
} = {
  conversationsMap: {},
  suggestedChatMap: {},
  conversationQueryMap: {},
}

const reducer = createReducer(initialState, (builder) => {
  builder.addCase(readAllConversations.fulfilled, (draft, { meta, payload }) => {
    const { arg: { creatorId } } = meta
    draft.conversationsMap[creatorId] = payload

    const newIds = payload
      .sort(({ timestamp: a }, { timestamp: b }) => b - a)
      .map(({ id }) => (id))
    updateConversationQueryMap(draft, creatorId, {
      conversationIds: [
        ...new Set([
          ...(draft.conversationQueryMap[creatorId]?.conversationIds || []),
          ...newIds,
        ]),
      ],
    })
  })

  builder.addCase(createConversation.fulfilled, (draft, { payload, meta: { arg: { creatorId } } }) => {
    if (!payload) return

    const existingConversations = draft.conversationsMap[creatorId] ?? []
    existingConversations.unshift(payload)
    updateConversationQueryMap(draft, creatorId, {
      conversationIds: [
        ...new Set([
          ...[payload.id],
          ...(draft.conversationQueryMap[creatorId]?.conversationIds || []),
        ]),
      ],
    })
  })

  builder.addCase(removeConversation.fulfilled, (draft, { meta }) => {
    const { arg: { creatorId, conversationId } } = meta
    const existingConversations = draft.conversationsMap[creatorId]
    if (!existingConversations) return

    draft.conversationsMap[creatorId] = existingConversations.filter(({ id }) => id !== conversationId)
  })

  builder.addCase(clearConversationUnread.fulfilled, (draft, { meta }) => {
    const { arg: { creatorId, conversationId } } = meta
    const existingConversations = draft.conversationsMap[creatorId]
    if (!existingConversations) return

    const existingConversation = existingConversations.find(({ id }) => id === conversationId)
    if (!existingConversation) return
    existingConversation.unread = 0
  })

  builder.addCase(upsertMessage, (draft, { payload }) => {
    const { creatorId, message } = payload
    if (!creatorId) return

    const existingConversations = draft.conversationsMap[creatorId]
    if (!existingConversations) return

    const conversation = existingConversations.find(({ id }) => id === Number(message.channel.channelID),
    )
    if (conversation) {
      conversation.lastMessage = message.content
      conversation.unread += 1
    } else {
      const conversationId = Number(message.channel.channelID)
      existingConversations.unshift({
        id: conversationId,
        lastMessage: message.content,
        timestamp: message.timestamp,
        unread: 1,
      })

      const newConversationIds = draft.conversationQueryMap[creatorId]?.conversationIds || []
      newConversationIds.unshift(conversationId)
      updateConversationQueryMap(draft, creatorId, {
        conversationIds: [...new Set(newConversationIds)],
      })
    }
  })

  builder.addCase(requestChatCompletions.pending, (draft, { meta }) => {
    const { creatorId, fanUserId } = meta.arg
    handleChatCompletionStatusChange(creatorId, fanUserId, draft, true)
  })

  builder.addCase(requestChatCompletions.rejected, (draft, { meta }) => {
    const { creatorId, fanUserId } = meta.arg
    handleChatCompletionStatusChange(creatorId, fanUserId, draft, false)
  })

  builder.addCase(
    requestChatCompletions.fulfilled,
    (draft, { meta, payload }) => {
      const { creatorId, fanUserId } = meta.arg
      const existSuggestedChatMap = draft.suggestedChatMap[creatorId]
      if (existSuggestedChatMap) {
        existSuggestedChatMap[fanUserId] = {
          suggestionFetching: false,
          ...payload,
        }
      } else {
        draft.suggestedChatMap[creatorId] = {
          [fanUserId]: { suggestionFetching: false, ...payload },
        }
      }
    },
  )

  builder.addCase(updateConversationQuery, (draft, { payload }) => {
    updateConversationQueryMap(draft, payload.creatorId, payload)
  })

  builder.addCase(updateHomeTabFanUserId, (draft, { payload }) => {
    const { creatorId, fanUserId } = payload
    if (fanUserId) {
      updateConversationQueryMap(draft, creatorId, {
        conversationIds: [
          ...new Set([
            ...(draft.conversationQueryMap[creatorId]?.conversationIds || []),
            ...[fanUserId],
          ]),
        ],
      })
    }
  })
})

export default { [conversationNamespace]: reducer }
